'use client'

import { QuestionIcon } from '@/_assets/icon-components'
import { IStocks, Locales } from '@/_types'
import { Link, useRouter } from '@/i18n/navigation'
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { EmblaOptionsType } from 'embla-carousel'
import ClassNames from 'embla-carousel-class-names'
import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import React, { useMemo } from 'react'

const OPTIONS: EmblaOptionsType = {
  align: 'start',
  slidesToScroll: 1,
}

interface StocksProps {
  promos?: { promos?: IStocks[]; count?: number | string } | null
}

function Stocks({ promos }: StocksProps) {
  const t = useTranslations()
  const locale = useLocale() as Locales
  const router = useRouter()

  const stocks = useMemo(() => {
    if (!promos?.promos || !Array.isArray(promos.promos)) {
      return []
    }
    // Filter only active stocks
    return promos.promos.filter((stock) => stock.is_active)
  }, [promos])

  const slides = useMemo(
    () =>
      stocks.map((stock) => {
        const title = stock.title?.[locale] || stock.title?.ru || ''

        const description =
          stock.description?.[locale] || stock.description?.ru || ''

        const imageUrl = stock.image?.[locale] || stock.image?.ru || ''

        const handleClick = () => {
          if (stock.button_link) {
            window.open(stock.button_link)
          } else if (stock.slug) {
            router.push(`/sale/${stock.slug}`)
          }
        }

        return (
          <Box className='embla__slide' key={stock.id}>
            <Box
              bg='white'
              borderRadius='20px'
              overflow='hidden'
              border={'1px solid #F4F4F4'}
              boxShadow='0px 0px 20px rgba(0, 0, 0, 0.06)'
              width={{ base: '325px', lg: '411px' }}
              flexShrink={0}
              cursor='pointer'
              height='100%'
              display='flex'
              flexDirection='column'
              p={3}
            >
              <Link href={`/sale/${stock?.slug}`}>
                <AspectRatio
                  gap={0}
                  h={{ base: '103px', lg: '129px' }}
                  w='100%'
                  ratio={113 / 84}
                  position='relative'
                >
                  {imageUrl ? (
                    <Image
                      src={process.env.BASE_URL ? imageUrl : ''}
                      alt={imageUrl}
                      style={{
                        borderRadius: '11px',
                      }}
                      fill
                    />
                  ) : (
                    <Box
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      borderRadius='12px'
                      bg='background'
                    >
                      <QuestionIcon />
                    </Box>
                  )}
                </AspectRatio>
              </Link>

              {/* Content */}
              <VStack
                flex='1'
                mt='3'
                gap={5}
                alignItems='flex-start'
                justifyContent='space-between'
              >
                <Stack gap={3}>
                  <Box
                    fontSize={{ base: 'lg', lg: 'xl' }}
                    fontWeight={700}
                    color='dark.cardTitle'
                    lineHeight={1.2}
                    lineClamp={1}
                    wordBreak='break-all'
                  >
                    {title}
                  </Box>
                  <Box
                    fontSize={{ base: 'sm', lg: 'sm' }}
                    fontWeight={400}
                    color='gray.800'
                    lineClamp={2}
                  >
                    {description}
                  </Box>
                </Stack>
                <Button
                  width='100%'
                  variant={'subtle'}
                  borderRadius='12px'
                  h='48px'
                  fontSize='sm'
                  fontWeight={600}
                  onClick={handleClick}
                  bg='product_button'
                  color='product_button.text'
                  _hover={{
                    bgColor: 'product_button.hover',
                  }}
                >
                  {stock.button_name?.[locale] || t('read_more')}
                </Button>
              </VStack>
            </Box>
          </Box>
        )
      }),
    [stocks, locale, router, t],
  )

  const [emblaRef] = useEmblaCarousel(OPTIONS, [
    ClassNames(),
    WheelGesturesPlugin(),
  ])

  if (!stocks || stocks.length === 0) {
    return null
  }

  return (
    <Box
      pt={{ base: 5, lg: 8 }}
      pb={{ base: 1, lg: 3 }} // extra padding added to slider wrapper to make the shadow visible
    >
      <Text
        fontSize={{ base: 'xl', lg: '2xl' }}
        px={{ base: 4, lg: 14 }}
        mb={{ lg: 1 }}
        fontWeight={700}
        color='#000'
      >
        {t('promotions_and_discounts') || 'Акции и скидки'}
      </Text>

      <Box className='embla embla--stocks'>
        <Box
          className='embla__viewport'
          px={{ base: 4, lg: 14 }}
          ref={emblaRef}
        >
          <Flex
            py={{ base: 4, lg: 5 }}
            gap={{ base: 2, lg: 4 }}
            className='embla__container'
          >
            {slides}
          </Flex>
        </Box>
      </Box>
    </Box>
  )
}

export default Stocks
